import { useState, useEffect, useCallback, useRef } from "react"
import { editorViewCtx } from "@milkdown/kit/core"
import { useInstance } from "@milkdown/react"
import { ExternalLink } from "lucide-react"

interface LinkPopoverState {
  visible: boolean
  href: string
  top: number
  left: number
}

const initialState: LinkPopoverState = {
  visible: false,
  href: "",
  top: 0,
  left: 0,
}

export function LinkPreviewPopover() {
  const [state, setState] = useState<LinkPopoverState>(initialState)
  const popoverRef = useRef<HTMLDivElement>(null)
  const [loading, getInstance] = useInstance()
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const hide = useCallback(() => {
    setState(initialState)
  }, [])

  useEffect(() => {
    const instance = getInstance()
    if (!instance || loading) return

    let dom: HTMLElement

    try {
      const ctx = instance.ctx
      const view = ctx.get(editorViewCtx)
      dom = view.dom
    } catch {
      return
    }

    function handleClick(e: MouseEvent) {
      const target = e.target as HTMLElement
      const anchor = target.closest("a") as HTMLAnchorElement | null
      if (!anchor) return

      // Don't intercept if text is selected (user is editing the link)
      const selection = window.getSelection()
      if (selection && !selection.isCollapsed) return

      e.preventDefault()
      e.stopPropagation()

      const rect = anchor.getBoundingClientRect()
      const href = anchor.getAttribute("href") || ""

      // Position above the link
      const popoverHeight = 36
      const gap = 6
      const top = rect.top - popoverHeight - gap
      const left = rect.left + rect.width / 2

      clearTimeout(hideTimeoutRef.current)

      setState({
        visible: true,
        href,
        top,
        left,
      })
    }

    function handleClickOutside(e: MouseEvent) {
      const popover = popoverRef.current
      if (popover && popover.contains(e.target as Node)) return

      // If clicking another link inside the editor, handleClick will fire
      const target = e.target as HTMLElement
      if (dom.contains(target) && target.closest("a")) return

      hide()
    }

    function handleScroll() {
      if (state.visible) hide()
    }

    dom.addEventListener("click", handleClick)
    document.addEventListener("mousedown", handleClickOutside)
    window.addEventListener("scroll", handleScroll, true)

    return () => {
      dom.removeEventListener("click", handleClick)
      document.removeEventListener("mousedown", handleClickOutside)
      window.removeEventListener("scroll", handleScroll, true)
      clearTimeout(hideTimeoutRef.current)
    }
  }, [loading, getInstance, hide, state.visible])

  function handleNavigate() {
    if (!state.href) return

    const isInternal = state.href.startsWith("https://braindown.app")

    if (isInternal) {
      window.location.href = state.href
    } else {
      window.open(state.href, "_blank", "noopener,noreferrer")
    }

    hide()
  }

  if (!state.visible) return null

  return (
    <div
      ref={popoverRef}
      className="fixed z-50 animate-in fade-in zoom-in-95 duration-100"
      style={{
        top: state.top,
        left: state.left,
        transform: "translateX(-50%)",
      }}
    >
      <button
        onClick={handleNavigate}
        className="flex items-center gap-1.5 max-w-xs px-2.5 py-1.5 rounded-lg bg-slate-900 dark:bg-slate-100 shadow-xl shadow-slate-900/20 dark:shadow-black/40 text-xs text-slate-200 dark:text-slate-700 hover:text-white dark:hover:text-slate-900 transition-colors cursor-pointer group"
      >
        <ExternalLink className="w-3 h-3 flex-shrink-0 text-slate-400 dark:text-slate-500 group-hover:text-indigo-400 dark:group-hover:text-indigo-500 transition-colors" strokeWidth={2} />
        <span className="truncate">{state.href}</span>
      </button>
    </div>
  )
}
